import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { LwButton, LwDataPanel, LwStatusPill, type LwStatusTone } from '@/components/lw';
import { resumeAppraisalFeeAlerts, type AppraisalFeeAlertStop } from '@/services/appraisalFeeComplianceService';
import { usesDisclosedAppraisalFee, appraisalFeeBasisLabel, type InvoiceFeeDisplay } from '../../../../../supabase/functions/_shared/invoice-fee-display-contract';
import { AppraisalFeeHistoryDrawer } from './AppraisalFeeHistoryDrawer';
import { FeeAuditChain } from './FeeAuditChain';
import { AWAITING_DISCLOSURE, feeAuditChain, feeTime } from './feeAuditChain';
import { StopFeeAlertsDialog } from './StopFeeAlertsDialog';

const money = (value: number | null) => value === null ? 'Not verified' : new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD'
}).format(value / 100);

export function feeAuditStatus(rows: InvoiceFeeDisplay[]): { title: string; tone: LwStatusTone; description: string } {
  const overages = rows.filter(row => row.outcome === 'overage');
  if (overages.length) {
    return { title: `${overages.length} fee ${overages.length === 1 ? 'overage' : 'overages'}`, tone: 'destructive',
      description: `${money(overages.reduce((sum, row) => sum + (row.excessCents || 0), 0))} above the last disclosed appraisal fee. Review the invoice findings below.` };
  }
  if (rows.some(row => row.outcome === 'review')) {
    return { title: 'Fee needs review', tone: 'warning', description: 'An invoice or its fee could not be verified confidently. Review findings are not emailed.' };
  }
  if (rows.some(row => row.needsAttention)) {
    return { title: 'Fee audit needs attention', tone: 'warning', description: 'The latest audit or invoice naming needs attention. Saved amounts remain available below.' };
  }
  if (rows.some(row => row.freshState === 'pending')) {
    return { title: 'Rerun in progress', tone: 'neutral', description: 'Waiting for a current invoice check.' };
  }
  if (rows.some(row => row.reasonCode === AWAITING_DISCLOSURE)) {
    return { title: 'Awaiting disclosure', tone: 'neutral', description: 'The invoice is saved. Comparison will run after a CD or LE appraisal fee is disclosed.' };
  }
  if (!rows.length || rows.some(row => row.pending || row.outcome === 'waiting' || !usesDisclosedAppraisalFee(row.ruleVersion))) {
    return { title: 'Fee verification pending', tone: 'neutral', description: 'Waiting for a current invoice check.' };
  }
  return { title: 'Invoice within last disclosed fee', tone: 'success', description: 'The verified invoice charges do not exceed the last disclosed appraisal fee.' };
}

type AlertStop = Pick<InvoiceFeeDisplay, 'alertsStoppedAt' | 'alertsStoppedBy' | 'alertsStoppedReason'>;

/** Stop context for the loan, from the newest row that carries it. */
export function feeAlertsStopped(rows: InvoiceFeeDisplay[]): AlertStop | null {
  const row = rows.find(item => item.alertsStoppedAt);
  return row ? { alertsStoppedAt: row.alertsStoppedAt, alertsStoppedBy: row.alertsStoppedBy, alertsStoppedReason: row.alertsStoppedReason } : null;
}

export function AppraisalFeeComplianceCard({ rows, orderId, selectedFindingId }: {
  rows: InvoiceFeeDisplay[];
  orderId?: string | null;
  selectedFindingId?: string;
}) {
  const [historyFor, setHistoryFor] = useState<InvoiceFeeDisplay | null>(null);
  const [stopping, setStopping] = useState(false);
  const [resuming, setResuming] = useState(false);
  // The snapshot is reloaded by its owner; keep the user's own change visible until then.
  const [override, setOverride] = useState<{ loanId: string; stop: AlertStop | null } | null>(null);
  const relevant = rows.filter(row => !row.orderId || row.orderId === orderId || row.id === selectedFindingId);
  const loanId = rows[0]?.loanId;
  const loanNumber = rows[0]?.loanNumber || 'this loan';
  const stopped = override && override.loanId === loanId ? override.stop : feeAlertsStopped(rows);
  const latestCures = [...rows].filter(row => row.cures).sort((a, b) =>
    (b.cures?.checkedAt || '').localeCompare(a.cures?.checkedAt || ''))[0]?.cures;
  const resume = async () => {
    if (!loanId || resuming) {
      return;
    }
    setResuming(true);
    try {
      await resumeAppraisalFeeAlerts(loanId);
      setOverride({ loanId, stop: null });
      toast.success(`Alerts resumed for ${loanNumber}.`, { position: 'top-right' });
    } catch (cause) {
      toast.error(cause instanceof Error ? cause.message : 'Alerts could not be resumed.', { position: 'top-right' });
    } finally {
      setResuming(false);
    }
  };
  const onStopped = (result: AppraisalFeeAlertStop) => {
    if (loanId) {
      setOverride({ loanId, stop: result });
    }
    setStopping(false);
  };
  return (
    <LwDataPanel title="Fee compliance" description="Saved invoice checks against the last disclosed appraisal fee: the CD when one has been disclosed, otherwise the LE. Variances require separate review of cure obligations.">
      {!relevant.length && <p className="appraisal-help">Waiting for a verified invoice for this appraisal.</p>}
      {relevant.map(row => {
        const chain = feeAuditChain(row);
        return (
          <div key={row.id} className="border-b border-border p-4 last:border-b-0" data-fee-finding={row.id}>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <strong className="text-sm">{row.invoiceFolder || 'Appraisal invoice'}</strong>
              <LwStatusPill label={row.outcome === 'overage' ? 'Fee overage' : row.outcome === 'passed' ? 'Within fee' : row.outcome === 'review' ? 'Needs review' : row.reasonCode === AWAITING_DISCLOSURE ? 'Awaiting disclosure' : 'Pending'}
                tone={row.outcome === 'overage' ? 'destructive' : row.outcome === 'passed' ? 'success' : row.outcome === 'review' ? 'warning' : 'neutral'} />
            </div>
            {!row.orderId && <p className="mb-2 text-xs text-muted-foreground">Loan-level finding · Appraisal assignment not yet verified.</p>}
            <dl className="space-y-2 text-sm">
              {[
                [appraisalFeeBasisLabel(row.ruleVersion, row.feeSource), row.feeCents === null ? 'Not available' : money(row.feeCents)],
                ['Amount on invoice', money(row.invoiceCents)],
                ['Excess', row.excessCents === null ? 'Not determined' : money(row.excessCents)]
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between gap-3"><dt className="text-muted-foreground">{label}</dt><dd className="whitespace-nowrap font-medium tabular-nums">{value}</dd></div>
              ))}
            </dl>
            {usesDisclosedAppraisalFee(row.ruleVersion) && <p className="mt-2 text-xs text-muted-foreground">
              Reference only: current fee {money(row.currentFeeCents ?? null)}{row.feeSource === 'CD' && ` · last disclosed LE ${money(row.lastDisclosedLECents ?? null)}`}.
            </p>}
            {row.invoiceAmountStatus === 'extracted' && <p className="mt-2 text-xs text-warning">Invoice amount extracted · verification needed.</p>}
            <p className="mt-2 break-words text-sm">{row.documentTitle}</p>
            {row.orderNumber && <p className="mt-1 text-xs text-muted-foreground">Order {row.orderNumber}</p>}
            {row.reason && <p className="mt-2 text-sm">{row.reason}</p>}
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <p className="appraisal-freshness" role={chain.busy ? 'status' : undefined}>
                {chain.busy
                  ? `${chain.kind === 'manual' ? 'Rerun' : 'Check'} started ${feeTime(chain.startedAt)}`
                  : row.checkedAt ? `Checked ${feeTime(row.checkedAt)}` : 'Not checked yet'}
              </p>
              <LwButton variant="sm" onClick={() => setHistoryFor(row)} aria-label={`Audit history for ${row.documentTitle}`}>History</LwButton>
            </div>
            <div className="mt-3">
              <FeeAuditChain chain={chain} compact ariaLabel={`Audit steps for ${row.documentTitle}`} />
            </div>
          </div>
        );
      })}
      {loanId && <div className="border-t border-border p-4" data-fee-alerts={stopped ? 'stopped' : 'active'}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm">
            <p className="font-medium">{stopped ? 'Alerts stopped' : 'Alerts on'}</p>
            <p className="text-xs text-muted-foreground">{stopped
              ? `${stopped.alertsStoppedBy || 'A user'} stopped overage emails and the morning digest ${feeTime(stopped.alertsStoppedAt)}. Findings stay visible here and in the report.`
              : 'Stop alerts when the fee is updated in Encompass but cannot be redisclosed. Findings stay visible.'}</p>
            {stopped?.alertsStoppedReason && <p className="mt-1 text-xs"><span className="text-muted-foreground">Reason:</span> {stopped.alertsStoppedReason}</p>}
          </div>
          {stopped
            ? <LwButton variant="sm" onClick={() => void resume()} aria-busy={resuming} aria-disabled={resuming} aria-label={`Resume alerts for ${loanNumber}`}>{resuming && <Loader2 className="animate-spin" size={14} aria-hidden="true" />}Resume alerts</LwButton>
            : <LwButton variant="sm" onClick={() => setStopping(true)} aria-label={`Stop alerts for ${loanNumber}`}>Stop alerts</LwButton>}
        </div>
      </div>}
      {latestCures && <div className="border-t border-border p-4">
        <p className="mb-2 text-sm font-medium">Loan cure amounts</p>
        <dl className="space-y-2 text-sm">
          {[
            ['Required cure', money(latestCures.requiredCents)],
            ['Applied cure', money(latestCures.appliedCents)]
          ].map(([label, value]) => <div key={label} className="flex justify-between gap-3"><dt className="text-muted-foreground">{label}</dt><dd className="whitespace-nowrap tabular-nums">{value}</dd></div>)}
        </dl>
        <p className="mt-2 text-xs text-muted-foreground">Loan totals for reference. These amounts do not settle an individual invoice finding.</p>
        {latestCures.checkedAt && <p className="mt-2 text-xs text-muted-foreground">Checked {feeTime(latestCures.checkedAt)} Pacific</p>}
      </div>}
      {historyFor && <AppraisalFeeHistoryDrawer onClose={() => setHistoryFor(null)}
        subject={rows.find(row => row.attachmentId === historyFor.attachmentId || row.id === historyFor.id) || historyFor} />}
      {stopping && loanId && <StopFeeAlertsDialog loanId={loanId} loanNumber={loanNumber} onClose={() => setStopping(false)} onStopped={onStopped} />}
    </LwDataPanel>
  );
}
